import React, { useContext, useEffect } from 'react';
import { Context } from '@front10/helpers/dist/Context';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import useStyles from '@front10/helpers/dist/UseStyles';
import { isSSR } from '@front10/utils';
import withCommonEMContainer from '../_commons/CommonEMContainer';
import ContainerErrorAlert from '../../alerts/ContainerErrorAlert';
//-------------------------------------
// Basic components here
//-------------------------------------
import Row from '../../elements/Row';
import Text from '../../elements/Text';
import Col from '../../elements/Col';
import Box from '../../elements/Box';
import Disclaimer from '../../others/Disclaimer';
import FareCalendar from '../../controls/FareFinderCalendar/FareCalendar';
import DayCard from '../../items/DayCardMobile/DayCardMobileCalendar';
import MonthNavigationTabs from '../../controls/FareFinderCalendar/atoms/MonthNavigationDropdown';
import Loading from '../../others/Loading';
import NoFaresBehavior from '../../others/NoFaresBehavior/NoFaresBehavior';
import Skeleton from '../../others/Skeleton/SkeletonFareCalendarOIMobile';
import useFiltersDisableSettings from './../InteractiveMapContainer/filters/useFiltersDisabled';
import SecondaryFilters from '../../controls/SecondaryFilters/SecondaryFilters';

//-------------------------------------
// Every mundo integration
//-------------------------------------
import { getHistograms } from '../../../_integrations/everymundo/histogram';
import { getAirports } from '../../../_integrations/everymundo/airports';

//-------------------------------------
// Commons functions
//-------------------------------------
import { useFilters, OriginDestinationLogic } from '../_commons/filters';
import { useFareItemProps } from '../_commons/tracking';
import InlineFilters from '../../controls/InlineFilters/InlineFiltersMobile';
import { getResultsLength } from '../_commons/calendar';

import prePopulationDefaultProps from '../_commons/prepopulationDefaultProps/histogram';
import HeaderContainer from '../../elements/HeaderContainer';
import Alert from '../../controls/FareFinderCalendar/atoms/Alert';

function LowFareCalendarOIContainerMobile(props) {
  const {
    prePopulation,
    integrationSettings,
    meta,
    pageContext,
    fareCalendar,
    fareItemOnClickAction,
    legend,
    showLoading,
    loading,
    direction,
    initialHistograms,
    getAirports: getAirportsFn,
    getHistograms: getHistogramsFn,
    showDisclaimer,
    showFilters,
    showTravelClassFilter,
    showSkeletonScreen,
    showLegend,
    skeletonSettings,
    travelClass: travelClassSettings,
    travelClassOptions,
    disclaimerContentSettings,
    mainContentSettings,
    LegendRowSettings,
    legendContentSettings,
    filterContentSettings,
    alertErrorInfo,
    alertErrorInfoTextSettings,
    minInitialResults,
    theme,
    filters,
    showSecondaryFilters = true,
    thresholdDay,
    // deprecated props
    origin,
    destination,
    disclaimer,
    originDestinationLogic,
    enableSEO = true
  } = props;

  const { translate } = useContext(Context);
  const [className, style] = useStyles(props, { direction, theme });
  const myStyle = Object.assign({ minHeight: '600px' }, style);
  const [activeTab, setActiveTab] = React.useState('start');
  const [isStartAndReturnDateSelected, setIsStartAndReturnDateSelected] = React.useState(false);

  // Define filter props using deprecated container props (for backwards compatibility)
  // Don't rely on this props for new configurations, they will be removed in a future version
  const filtersConfig = {
    availableFilters: showTravelClassFilter
      ? ['OriginDestination', 'TravelClass']
      : ['OriginDestination'],
    containerSettings: filterContentSettings,
    originDestinationSettings: {
      components: {
        column: {
          justifyContent: 'center'
        },
        origin,
        destination
      },
      showOrigin: true,
      showDestination: true
    },
    travelClassSettings: {
      ...travelClassSettings,
      ...travelClassOptions
    },
    clearButtonDisplay: 'hidden',
    ...filters
  };

  const initialMonth = dayjs().add(prePopulation.dateRange.start, 'day').toDate();

  const thresholdDayEnable = Boolean(
    thresholdDay && thresholdDay && dayjs(initialMonth).date() >= thresholdDay
  );
  const {
    fares: { match, ...fares },
    retry,
    dispatch,
    tracking,
    ...filtersState
  } = useFilters({
    originDestinationLogic,
    filter: {
      prePopulation,
      integrationSettings,
      meta,
      pageContext,
      getAirports: getAirportsFn,
      maxItemsAmount: 1,
      nextMonth: thresholdDayEnable
    },
    initialData: initialHistograms,
    getFares: getHistogramsFn,
    faresTransformationLogic: 'histogram',
    minInitialResults,
    showNoResultsPlaceholder: false,
    availableFilters: showFilters ? filtersConfig.availableFilters : [],
    hideBudgetOnMultiCurrency: filtersConfig.hideBudgetOnMultiCurrency,
    routeMode: true,
    getResultsLength,
    showOrigin:
      filtersConfig.originDestinationSettings && filtersConfig.originDestinationSettings.showOrigin,
    showDestination:
      filtersConfig.originDestinationSettings &&
      filtersConfig.originDestinationSettings.showDestination,
    enabledhistogram: true
  });

  useEffect(() => {
    dispatch({
      type: 'DATE_RANGE_HISTOGRAM_TYPE_CHANGE',
      payload: {
        dateRange: {
          start: filtersState.dateRange.start,
          end: filtersState.dateRange.end,
          selected: undefined
        },
        histogramType: ''
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isOriginSelected = Boolean(filtersState.locations.origin.value);
  const isDestinationSelected = Boolean(filtersState.locations.destination.value);
  const secondaryFiltersDisableSettings = useFiltersDisableSettings(
    { ...filters, ...props.secondaryFilters },
    {
      budget: !isOriginSelected || !isDestinationSelected,
      flightType: !isOriginSelected || !isDestinationSelected,
      travelClass: !isOriginSelected || !isDestinationSelected,
      travelerInterest: !isOriginSelected || !isDestinationSelected,
      flightStopCount: !isOriginSelected || !isDestinationSelected
    }
  );

  const renderSecondaryFilters = showSecondaryFilters && (
    <SecondaryFilters
      {...filters}
      {...filtersState}
      {...props.secondaryFilters}
      {...secondaryFiltersDisableSettings}
      filter={{ ...{ prePopulation, integrationSettings, meta, getAirports } }}
    />
  );

  const { dateRange } = filtersState;

  const dayCard = useFareItemProps(fareCalendar.dayCard, dispatch, fareItemOnClickAction);

  if (isSSR() && !Boolean(enableSEO)) {
    return null;
  }

  if (match('HIDDEN')) {
    return <NoFaresBehavior />;
  }

  const changeMonth = (month) => {
    dispatch({ type: 'MONTH_CHANGE_HISTOGRAM', payload: month });
  };

  function handleSeeMoreDeals() {
    tracking({ type: 'MORE_DEALS' });
    return true;
  }

  const header = (
    <>
      {showSkeletonScreen && match('SKELETON') && (
        <Skeleton {...skeletonSettings} position="absolute" width="100" />
      )}
      {showLoading && match('LOADING') && <Loading {...loading} />}
      <HeaderContainer
        defaultTitleValue={'LOWEST DAILY FARES FOR THE UPCOMING 90 DAYS'}
        handleSeeMoreDeals={handleSeeMoreDeals}
        {...props}
      />
    </>
  );

  const showAlertInfo = filters.showAlertInfo ?? true;
  const renderAlertErrorInfo = showAlertInfo && (!isOriginSelected || !isDestinationSelected) && (
    <Box tabIndex="0" {...alertErrorInfo}>
      <Text {...alertErrorInfoTextSettings}>
        {translate(
          'cmp-low-fare-calendar-alert-error-info-mobile',
          'Please select a route (origin and destination) in order to get results'
        )}
      </Text>
    </Box>
  );

  const renderRoundTripAlertInfo = isOriginSelected &&
    isDestinationSelected &&
    filtersState.flightType.value === 'ROUND_TRIP' &&
    !match('ERROR') &&
    !isStartAndReturnDateSelected && <Alert {...props.alertInfo} activeTab={activeTab} />;

  const renderOneWayAlertInfo = isOriginSelected &&
    isDestinationSelected &&
    !match('ERROR') &&
    filtersState.flightType.value === 'ONE_WAY' && <Alert {...props.alertInfo} />;

  return (
    <InlineFilters
      className={`${className} ${LowFareCalendarOIContainerMobile.componentName}`}
      style={myStyle}
      dataTestId="lowFareCalendarOIContainerMobile"
      header={header}
      {...filtersConfig}
      {...filtersState}
    >
      {renderSecondaryFilters}
      {renderAlertErrorInfo}
      {renderRoundTripAlertInfo}
      {renderOneWayAlertInfo}
      <Row {...mainContentSettings} style={{ position: 'relative' }}>
        {(match('WAITING_FOR_DESTINATION') ||
          !isOriginSelected ||
          match('ERROR') ||
          !isDestinationSelected) && (
          <>
            <Col justifyContent="center" display="flex">
              <ContainerErrorAlert
                {...props.alert}
                labelKeySubTitleIntro={undefined}
                showIcon={false}
                showMessage={false}
                labelKeySubTitle={''}
              />
            </Col>
          </>
        )}
        {match('COMPLETE') && (
          <>
            <Col justifyContent="center" display="flex">
              <FareCalendar
                {...fareCalendar}
                dayCard={dayCard}
                initialMonth={initialMonth}
                currentMonth={dateRange.value.start}
                endDateMonth={dateRange.value.end}
                data={fares.data}
                NavComponentInstance={MonthNavigationTabs}
                onSelectMonth={changeMonth}
                loading={match('LOADING')}
                DayComponentInstance={DayCard}
                isDesktop={false}
                filtersState={filtersState}
                globalDispatch={dispatch}
                showLegend={showLegend && !match('ERROR')}
                LegendRowSettings={LegendRowSettings}
                legendContentSettings={legendContentSettings}
                legend={legend}
                setActiveTab={setActiveTab}
                setIsStartAndReturnDateSelected={setIsStartAndReturnDateSelected}
              />
            </Col>
          </>
        )}
      </Row>
      {showDisclaimer && (
        <Row {...disclaimerContentSettings}>
          <Col>
            <Disclaimer labelKey="cmp-low-calendar-container-disclaimer" {...disclaimer} />
          </Col>
        </Row>
      )}
    </InlineFilters>
  );
}

LowFareCalendarOIContainerMobile.propTypes = {
  /**
   * filterContentSettingsRow props
   */
  filterContentSettingsRow: PropTypes.objectOf(PropTypes.any),
  /**
   * headerContentSettings props
   */
  headerContentSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * Props of alerts
   */
  alert: PropTypes.objectOf(PropTypes.any),
  /**
   * To be used to set if t container is a controlled one
   */
  containerType: PropTypes.string,
  /**
   * Destination Component
   */
  destination: PropTypes.objectOf(PropTypes.any),
  /**
   * Direction of the component rtl o ltr
   */
  direction: PropTypes.oneOf(['rtl', 'ltr']),
  /**
   * Disclaimer Component
   */
  disclaimer: PropTypes.objectOf(PropTypes.any),
  /**
   * disclaimerContentSettings props
   */
  disclaimerContentSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * fareCalendar Component props
   */
  fareCalendar: PropTypes.objectOf(PropTypes.any),
  /**
   * Possible interactions will be Popup or Direct Deeplink
   */
  fareItemInteractionType: PropTypes.oneOf(['popup', 'deeplink']),
  /**
   * Handle the action when click on card
   */
  fareItemOnClickAction: PropTypes.func,
  /**
   * filterContentSettings props
   */
  filterContentSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * Filters settings
   */
  filters: PropTypes.objectOf(PropTypes.any),
  /**
   * Function for request to obtain airports
   */
  getAirports: PropTypes.func,
  /**
   * Function for request to obtain histograms
   */
  getHistograms: PropTypes.func,
  /**
   * headerLocation
   */
  headerLocation: PropTypes.objectOf(PropTypes.any),
  /**
   * Hide the component if the ammount of initial results is lower than this value
   */
  minInitialResults: PropTypes.number,
  /**
   * Used to prepopulate the component
   */
  initialHistograms: PropTypes.arrayOf(PropTypes.any),
  /**
   * All integration configurations
   */
  integrationSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * legend Component props
   */
  legend: PropTypes.objectOf(PropTypes.any),
  /**
   * legend content settings
   */
  legendContentSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * Loading Component
   */
  loading: PropTypes.objectOf(PropTypes.any),
  /**
   * mainContentSettings props
   */
  mainContentSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * Props of noResultsPlaceHolder
   */
  noResultsPlaceHolder: PropTypes.objectOf(PropTypes.any),
  /**
   * Origin Component
   */
  origin: PropTypes.objectOf(PropTypes.any),
  /**
   * Page Context
   */
  pageContext: PropTypes.shape({
    deviceCategory: PropTypes.string,
    siteEdition: PropTypes.string
  }),
  /**
   * Component PrePopulation
   */
  prePopulation: PropTypes.objectOf(PropTypes.any),
  /**
   * Show Airport Iata Code in locations
   */
  showAirportIataCode: PropTypes.bool,
  /**
   * Determine if the component will show disclaimer
   */
  showDisclaimer: PropTypes.bool,
  /**
   * showFilters
   */
  showFilters: PropTypes.bool,
  /**
   * Determine if the component will show the travelClass filter
   */
  showTravelClassFilter: PropTypes.bool,
  /**
   * showCarouselMode
   */
  showCarouselMode: PropTypes.bool,
  /**
   * Determine if the component will show the skeleton screen while
   * loading (Default value will be false)
   */
  showSkeletonScreen: PropTypes.bool,
  /**
   * showLegend
   */
  showLegend: PropTypes.bool,
  /**
   * Determine if the component will show the title
   */
  showTitle: PropTypes.bool,
  /**
   * SkeletonSettings Component
   */
  skeletonSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * Shows Loading Component
   */
  showLoading: PropTypes.bool,
  /**
   * Title Component
   */
  title: PropTypes.objectOf(PropTypes.any),
  /**
   * titleKey of the component
   */
  titleKey: PropTypes.string,
  /**
   * Function to translate the component
   */
  translate: PropTypes.func,
  /**
   * travelClass options
   */
  travelClassOptions: PropTypes.objectOf(PropTypes.any),
  /**
   * travelClass of the component
   */
  travelClass: PropTypes.objectOf(PropTypes.any),
  /**
   * Parameters tracking
   */
  tracking: PropTypes.objectOf(PropTypes.any),
  /**
   * LocalizationData via moment to be used as a date translator
   * https://github.com/moment/moment/blob/develop/src/locale/es.js
   */
  localizationData: PropTypes.objectOf(PropTypes.any),
  /**
   * Theme object on the component
   */
  theme: PropTypes.objectOf(PropTypes.any),
  /**
   * Metadata settings
   */
  meta: PropTypes.objectOf(PropTypes.any),
  /**
   * Login to load origin and destination airports
   * same: Origin and Destination are pre-populated or not
   *      a single airport service call is done to fill out the Origin
   *      and Destinations filters (for performance purposes).
   * different: Origin and Destination are pre-populated or not
   *      2 airports service calls are done to fill out the Origin
   *      and Destinations filters.
   */
  originDestinationLogic: PropTypes.oneOf([
    OriginDestinationLogic.SAME,
    OriginDestinationLogic.DIFFERENT
  ]),
  /**
   * Show the SubTitle Component
   */
  showSubTitle: PropTypes.bool,
  /**
   * subHeaderContentSettings props
   */
  subHeaderContentSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * titleKey of the component
   */
  subTitleKey: PropTypes.string,
  /**
   * SubTitle Component
   */
  subTitleSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * thresholdDay indicate past how many days the low fare calendar must display the upcoming month
   */
  thresholdDay: PropTypes.number
};
LowFareCalendarOIContainerMobile.defaultProps = {
  filterContentSettingsRow: {},
  headerContentSettings: {},
  alert: {
    bgColor: 'secondary-4',
    textAlign: 'center',
    padding: '3',
    rounded: '@theme:card.rounded'
  },
  containerType: 'stateful',
  destination: {},
  direction: 'ltr',
  disclaimer: {
    fontSize: '3'
  },
  disclaimerContentSettings: {
    marginTop: '3'
  },
  fareCalendar: {},
  fareItemInteractionType: 'popup',
  fareItemOnClickAction: null,
  filterContentSettings: {
    marginTop: '2'
  },
  filters: {},
  getAirports,
  getHistograms,
  headerLocation: {},
  minInitialResults: null,
  initialHistograms: null,
  integrationSettings: {},
  legend: {},
  legendContentSettings: {
    marginTop: '3'
  },
  loading: {},
  mainContentSettings: {
    marginTop: '3'
  },
  noResultsPlaceHolder: {
    bgColor: 'light'
  },
  origin: {},
  pageContext: {
    deviceCategory: 'DESKTOP',
    siteEdition: 'en_HK'
  },
  prePopulation: prePopulationDefaultProps,
  showAirportIataCode: true,
  showDisclaimer: true,
  showFilters: true,
  showTravelClassFilter: false,
  showCarouselMode: true,
  showSkeletonScreen: true,
  showLegend: true,
  showTitle: true,
  skeletonSettings: {
    style: { backgroundColor: 'white', paddingTop: '0.5rem', paddingBottom: '3.25rem' }
  },
  showLoading: true,
  title: {},
  titleKey: 'cmp-low-calendar-container-title',
  translate: () => {},
  travelClassOptions: {
    options: [
      { label: 'economy', value: 'ECONOMY' },
      { label: 'business', value: 'BUSINESS' },
      { label: 'first', value: 'FIRST' }
    ]
  },
  travelClass: {},
  tracking: {
    flexibleDates: false,
    passengerCount: 1,
    promoCode: 'EVERYMUNDO',
    eventType: 'fareListLowFareCalendar'
  },
  localizationData: null,
  theme: null,
  meta: null,
  originDestinationLogic: OriginDestinationLogic.SAME,
  showSubTitle: false,
  subHeaderContentSettings: {},
  subTitleKey: 'cmp-low-fare-calendar-sub-title',
  subTitleSettings: {},
  thresholdDay: 0
};
LowFareCalendarOIContainerMobile.componentName = 'LowFareCalendarOIContainerMobile';

// HOC with common functionality to all containers
export default withCommonEMContainer(LowFareCalendarOIContainerMobile);
